/**
 * Orderbook Depth Modal Component
 *
 * Displays detailed orderbook depth for both spot and futures markets
 * with real-time updates and arbitrage analysis.
 *
 * Features:
 * - On-demand orderbook subscription
 * - Top 10 bid/ask levels for spot and futures
 * - Spread and profit calculation with fees
 * - Real-time updates while modal is open
 * - Automatic cleanup on close
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Skeleton,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useOrderbookSubscriptions, type OrderBook } from '../../hooks/useOrderbookSubscriptions';
import {
  calculateLongArbitrage,
  calculateShortArbitrage,
  formatCurrency,
  formatPercentage,
  getProfitColor,
  type ArbitrageOpportunity,
} from '../../utils/arbitrageCalculations';

/**
 * Component props
 */
export interface OrderbookDepthModalProps {
  /** Whether modal is open */
  open: boolean;

  /** Callback to close modal */
  onClose: () => void;

  /** Trading pair symbol (e.g., "BTCUSDT") */
  symbol: string;

  /** Spot exchange name (e.g., "Binance", "Upbit", "Bithumb") */
  spotExchange: string;

  /** Futures exchange name (always "Binance Futures") */
  futuresExchange: string;
}

/**
 * Orderbook Depth Modal Component
 */
export const OrderbookDepthModal: React.FC<OrderbookDepthModalProps> = ({
  open,
  onClose,
  symbol,
  spotExchange,
  futuresExchange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Subscribe to orderbooks (enabled when modal is open)
  const { spotOrderbook, futuresOrderbook } = useOrderbookSubscriptions(
    symbol,
    spotExchange,
    open // Subscribe when modal is open, cleanup when closed
  );

  /**
   * Format price with appropriate decimal places
   */
  const formatPrice = (price: number, decimals: number = 2): string => {
    return price.toFixed(decimals);
  };

  /**
   * Format volume with appropriate decimal places
   */
  const formatVolume = (volume: number): string => {
    return volume.toFixed(8);
  };

  /**
   * Get bid/ask color
   */
  const getBidColor = () => theme.palette.success.main;
  const getAskColor = () => theme.palette.error.main;

  /**
   * Render orderbook table
   */
  const renderOrderbookTable = (
    data: OrderBook | null,
    type: 'spot' | 'futures',
    exchangeName: string
  ) => {
    if (!data) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={400} />
        </Box>
      );
    }

    return (
      <Box>
        {/* Exchange badge */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Chip
            label={exchangeName}
            color="primary"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
            }}
          />
        </Box>

        {/* Asks table (reversed order - highest price first) */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: getAskColor(), fontWeight: 'bold' }}>
          Asks (Sell Orders)
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Volume</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.asks.slice(0, 10).reverse().map((level, index) => (
                <TableRow
                  key={`ask-${index}`}
                  sx={{
                    backgroundColor: `${getAskColor()}10`,
                  }}
                >
                  <TableCell align="right" sx={{ color: getAskColor(), fontWeight: 'bold' }}>
                    {formatPrice(level.price, 2)}
                  </TableCell>
                  <TableCell align="right">{formatVolume(level.volume)}</TableCell>
                  <TableCell align="right">{formatVolume(level.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bids table */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: getBidColor(), fontWeight: 'bold' }}>
          Bids (Buy Orders)
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 200, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Volume</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.bids.slice(0, 10).map((level, index) => (
                <TableRow
                  key={`bid-${index}`}
                  sx={{
                    backgroundColor: `${getBidColor()}10`,
                  }}
                >
                  <TableCell align="right" sx={{ color: getBidColor(), fontWeight: 'bold' }}>
                    {formatPrice(level.price, 2)}
                  </TableCell>
                  <TableCell align="right">{formatVolume(level.volume)}</TableCell>
                  <TableCell align="right">{formatVolume(level.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  /**
   * Render arbitrage opportunity card
   */
  const renderArbitrageCard = (
    opportunity: ArbitrageOpportunity | null,
    type: 'long' | 'short',
    title: string
  ) => {
    if (!opportunity) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      );
    }

    const color = getProfitColor(opportunity.netProfitPercentage);
    const bgColor =
      color === 'success' ? theme.palette.success.main + '20' :
      color === 'warning' ? theme.palette.warning.main + '20' :
      theme.palette.error.main + '20';

    return (
      <Paper
        sx={{
          p: 2,
          background: bgColor,
          border: `2px solid ${
            color === 'success' ? theme.palette.success.main :
            color === 'warning' ? theme.palette.warning.main :
            theme.palette.error.main
          }`,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {title}
        </Typography>

        <Grid container spacing={2}>
          {/* Entry Price */}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Entry Price ({type === 'long' ? 'Spot Ask' : 'Spot Bid'})
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              ${opportunity.entryPrice.toFixed(2)}
            </Typography>
          </Grid>

          {/* Exit Price */}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Exit Price ({type === 'long' ? 'Futures Bid' : 'Futures Ask'})
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              ${opportunity.exitPrice.toFixed(2)}
            </Typography>
          </Grid>

          {/* Gross Spread */}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Gross Spread
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatCurrency(opportunity.grossSpread)} ({formatPercentage(opportunity.grossSpreadPercentage)})
            </Typography>
          </Grid>

          {/* Total Fees */}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Total Fees
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatCurrency(opportunity.fees.totalFee)} ({formatPercentage(opportunity.fees.totalFeePercentage)})
            </Typography>
          </Grid>

          {/* Net Profit */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Net Profit (After Fees)
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color:
                    color === 'success' ? theme.palette.success.main :
                    color === 'warning' ? theme.palette.warning.main :
                    theme.palette.error.main,
                }}
              >
                {formatCurrency(opportunity.netProfit)} ({formatPercentage(opportunity.netProfitPercentage)})
              </Typography>
              {!opportunity.isProfitable && (
                <Chip
                  label="⚠️ Low Liquidity - Check Volume"
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  /**
   * Calculate arbitrage opportunities
   */
  const calculateOpportunities = (): {
    long: ArbitrageOpportunity | null;
    short: ArbitrageOpportunity | null;
  } => {
    if (!spotOrderbook || !futuresOrderbook) {
      return { long: null, short: null };
    }

    const spotBestAsk = spotOrderbook.asks[0];
    const spotBestBid = spotOrderbook.bids[0];
    const futuresBestAsk = futuresOrderbook.asks[0];
    const futuresBestBid = futuresOrderbook.bids[0];

    if (!spotBestAsk || !spotBestBid || !futuresBestAsk || !futuresBestBid) {
      return { long: null, short: null };
    }

    const long = calculateLongArbitrage(
      spotBestAsk.price,
      futuresBestBid.price,
      spotExchange,
      futuresExchange
    );

    const short = calculateShortArbitrage(
      spotBestBid.price,
      futuresBestAsk.price,
      spotExchange,
      futuresExchange
    );

    return { long, short };
  };

  const opportunities = calculateOpportunities();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '90%',
          maxWidth: isMobile ? '100%' : 1200,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          boxShadow: `0 0 40px ${theme.palette.primary.main}40`,
        },
      }}
    >
      {/* Title */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          {symbol} Orderbook Depth
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            '&:hover': {
              backgroundColor: `${theme.palette.error.main}20`,
              color: theme.palette.error.main,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers>
        {/* Orderbooks */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Spot orderbook */}
          <Grid item xs={12} md={6}>
            {renderOrderbookTable(spotOrderbook, 'spot', spotExchange)}
          </Grid>

          {/* Futures orderbook */}
          <Grid item xs={12} md={6}>
            {renderOrderbookTable(futuresOrderbook, 'futures', futuresExchange)}
          </Grid>
        </Grid>

        {/* Arbitrage Analysis Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Arbitrage Analysis
          </Typography>

          <Grid container spacing={2}>
            {/* Long Opportunity */}
            <Grid item xs={12} md={6}>
              {renderArbitrageCard(
                opportunities.long,
                'long',
                'Long Arbitrage (Buy Spot, Sell Futures)'
              )}
            </Grid>

            {/* Short Opportunity */}
            <Grid item xs={12} md={6}>
              {renderArbitrageCard(
                opportunities.short,
                'short',
                'Short Arbitrage (Sell Spot, Buy Futures)'
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
